/**
 * Packagings Service
 *
 * API calls for Packagings.
 * @see US-8.21 - Gestion des Conditionnements
 */

import { apiClient } from '@/api/client';
import type {
  Packaging,
  PackagingWithUsageCount,
  CreatePackagingInput,
  UpdatePackagingInput,
  PackagingsListResponse,
  PackagingDetailResponse,
  PackagingCreateResponse,
  PackagingUpdateResponse,
  PackagingDeleteResponse,
  PackagingOption,
  ApiSuccess,
} from '@bstock/shared';

// Re-export types for convenience
export type {
  Packaging,
  PackagingWithUsageCount,
  CreatePackagingInput,
  UpdatePackagingInput,
  PackagingOption,
};

export const packagingsService = {
  /**
   * List all packagings
   */
  async list(includeInactive = false): Promise<PackagingsListResponse> {
    const response = await apiClient.get<ApiSuccess<PackagingsListResponse>>('/packagings', {
      params: { includeInactive },
    });
    return response.data.data;
  },

  /**
   * Get single packaging by ID
   */
  async getById(id: string): Promise<PackagingDetailResponse> {
    const response = await apiClient.get<ApiSuccess<PackagingDetailResponse>>(`/packagings/${id}`);
    return response.data.data;
  },

  /**
   * Create a new packaging
   */
  async create(data: CreatePackagingInput): Promise<PackagingCreateResponse> {
    const response = await apiClient.post<ApiSuccess<PackagingCreateResponse>>('/packagings', data);
    return response.data.data;
  },

  /**
   * Update a packaging
   */
  async update(id: string, data: UpdatePackagingInput): Promise<PackagingUpdateResponse> {
    const response = await apiClient.put<ApiSuccess<PackagingUpdateResponse>>(
      `/packagings/${id}`,
      data
    );
    return response.data.data;
  },

  /**
   * Deactivate a packaging (soft delete)
   */
  async deactivate(id: string): Promise<PackagingDeleteResponse> {
    const response = await apiClient.delete<ApiSuccess<PackagingDeleteResponse>>(
      `/packagings/${id}`
    );
    return response.data.data;
  },

  /**
   * Activate a packaging
   */
  async activate(id: string): Promise<PackagingDeleteResponse> {
    const response = await apiClient.post<ApiSuccess<PackagingDeleteResponse>>(
      `/packagings/${id}/activate`
    );
    return response.data.data;
  },

  /**
   * Get active packagings for dropdown
   */
  async getActive(): Promise<PackagingOption[]> {
    const response = await apiClient.get<ApiSuccess<PackagingOption[]>>('/packagings/active');
    return response.data.data;
  },
};
